import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import * as styles from './index.module.css';
import useMenuPosition from './useMenuPosition';

function hasSubmenu(action) {
  return typeof action.getSubmenuItems === 'function';
}

export const ActionsCell = React.memo(({ row, actionsMenu }) => {
  const [open, setOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const submenuRef = useRef(null);
  const itemRefs = useRef({});
  const submenuTriggerRef = useRef(null);
  const submenuHideTimeout = useRef(null);

  // Filter visible actions upfront to determine if we should render the button
  // Guard against action.hidden being a boolean or a function
  const visibleActions = actionsMenu.filter((action) => {
    if (typeof action.hidden === 'function') {
      return !action.hidden(row);
    }
    return !action.hidden;
  });

  const estimatedItemHeight = 44;
  const estimatedMenuHeight = visibleActions.length * estimatedItemHeight;

  // Use hook for menu positioning
  const getMenuPosition = useMenuPosition({
    triggerRef: buttonRef,
    menuRef,
    isOpen: open,
    estimatedHeight: estimatedMenuHeight,
    rightOffset: 6,
    alignTopWithTriggerTop: true,
  });

  // Calculate estimated submenu height
  const hoveredAction = hoveredItem
    ? actionsMenu.find((a) => a.key === hoveredItem)
    : null;
  const submenuItems = hoveredAction?.getSubmenuItems?.(row) || [];
  const estimatedSubmenuHeight = submenuItems.length * estimatedItemHeight;

  // Track the trigger element to force position recalculation
  const [submenuTriggerElement, setSubmenuTriggerElement] = useState(null);

  // Update submenu trigger ref when hoveredItem changes
  useEffect(() => {
    if (hoveredItem && itemRefs.current[hoveredItem]) {
      const element = itemRefs.current[hoveredItem];
      submenuTriggerRef.current = element;
      setSubmenuTriggerElement(element);
    } else {
      submenuTriggerRef.current = null;
      setSubmenuTriggerElement(null);
    }
  }, [hoveredItem]);

  // Use hook for submenu positioning
  const getSubmenuPosition = useMenuPosition({
    triggerRef: submenuTriggerRef,
    menuRef: submenuRef,
    isOpen: !!hoveredItem,
    estimatedHeight: estimatedSubmenuHeight,
    rightOffset: 0,
    alignTopWithTriggerTop: true, // Submenu aligns with item top
    triggerElement: submenuTriggerElement,
  });

  const handleButtonClick = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  // Handle click outside to close menu
  useEffect(() => {
    if (!open) {
      setHoveredItem(null);
      return undefined;
    }

    const handleClickOutside = (e) => {
      const clickedMenu = menuRef.current?.contains(e.target);
      const clickedSubmenu = submenuRef.current?.contains(e.target);

      if (!clickedMenu && !clickedSubmenu) {
        setOpen(false);
        setHoveredItem(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(
    () => () => {
      if (submenuHideTimeout.current) {
        clearTimeout(submenuHideTimeout.current);
      }
    },
    [],
  );

  // Don't render the 3-dot button if there are no visible actions
  if (visibleActions.length === 0) {
    return null;
  }

  const clearHideTimeout = () => {
    if (submenuHideTimeout.current) {
      clearTimeout(submenuHideTimeout.current);
      submenuHideTimeout.current = null;
    }
  };

  const handleMouseEnter = (action, key) => {
    clearHideTimeout();

    if (hasSubmenu(action)) {
      setHoveredItem(key);
    } else {
      setHoveredItem(null);
    }
  };

  const handleMouseLeave = () => {
    submenuHideTimeout.current = setTimeout(() => {
      setHoveredItem(null);
    }, 100);
  };

  const handleActionClick = (action) => {
    if (hasSubmenu(action)) return;
    // Check if action is disabled
    const isDisabled =
      typeof action.disabled === 'function'
        ? action.disabled(row)
        : action.disabled;
    if (isDisabled) return;
    action.onClick?.(row);
    setOpen(false);
    setHoveredItem(null);
  };

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        onClick={handleButtonClick}
        className={styles['actions-button']}
        aria-label="actions"
      >
        <span className={styles['actions-dots-container']}>
          {[0, 1, 2].map((i) => (
            <span key={i} className={styles['actions-dot']} />
          ))}
        </span>
      </button>
      {open &&
        ReactDOM.createPortal(
          <ul
            ref={menuRef}
            className={styles['actions-menu']}
            style={getMenuPosition()}
          >
            {visibleActions.map((action) => (
                <li
                  key={action.key}
                  ref={(el) => {
                    itemRefs.current[action.key] = el;
                  }}
                  className={styles['actions-menu-item']}
                  onClick={() => handleActionClick(action)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleActionClick(action);
                    }
                  }}
                  onMouseEnter={() => handleMouseEnter(action, action.key)}
                  onMouseLeave={handleMouseLeave}
                  role="menuitem"
                  tabIndex={0}
                >
                  {typeof action.component === 'function'
                    ? action.component(row)
                    : action.component}
                  {hasSubmenu(action) && (
                    <span className={styles['submenu-indicator']}>▸</span>
                  )}
                </li>
              ))}
          </ul>,
          document.body,
        )}
      {hoveredItem &&
        (() => {
          if (!hasSubmenu(hoveredAction)) return null;

          if (!submenuItems || submenuItems.length === 0) return null;

          const handleSubmenuItemClick = (item) => {
            if (hasSubmenu(item)) return;
            item.onClick?.(row);
            setOpen(false);
            setHoveredItem(null);
          };

          return ReactDOM.createPortal(
            <ul
              ref={submenuRef}
              className={styles['actions-submenu']}
              style={getSubmenuPosition()}
              onMouseEnter={clearHideTimeout}
              onMouseLeave={handleMouseLeave}
            >
              {submenuItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <li
                    key={item.label}
                    className={styles['submenu-item']}
                    onClick={() => handleSubmenuItemClick(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSubmenuItemClick(item);
                      }
                    }}
                    role="menuitem"
                    tabIndex={0}
                    aria-label={item.label}
                  >
                    {IconComponent && (
                      <IconComponent className={styles['submenu-item-icon']} />
                    )}
                    <span>{item.label}</span>
                    {hasSubmenu(item) && (
                      <span className={styles['submenu-indicator']}>▸</span>
                    )}
                  </li>
                );
              })}
            </ul>,
            document.body,
          );
        })()}
    </>
  );
});
